import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { type LatitudeLongitude } from './types/common';
import { ReverseGeocodingLocationItem, ReverseGeocodingResponse } from './types/reverse-geocoding';
import { GetAddressesItem, GetAddressesResponse, GetAddressesArguments } from './types/get-addresses';
import {
  MultipleReverseGeocodingResult,
  MultipleReverseGeocodingResponse,
  MultipleReverseGeocodingArguments,
} from './types/multiple-reverse-geocoding';
import { LookupAddressResponse, LookupAddressArguments } from './types/lookup-address';
import {
  AUTOCOMPLETE_ENDPOINT,
  HERE_MAPS_API_KEY,
  LOOKUP_ENDPOINT,
  REVERSE_GEOCODE_ENDPOINT,
  MULTIPLE_REVERSE_GEOCODE_ENDPOINT,
  USER_LANGUAGE,
} from '@/app/config';

const baseQuery = fetchBaseQuery({
  baseUrl: '',
});

const baseQueryRetry = retry(baseQuery, {
  maxRetries: 0,
});

const getAutocompleteUrl = ({ searchTerm }: { searchTerm: string }): string => {
  const searchParameters = new URLSearchParams();
  searchParameters.set('lang', USER_LANGUAGE);
  searchParameters.set('q', searchTerm);
  searchParameters.set('limit', '4');
  searchParameters.set('apiKey', HERE_MAPS_API_KEY);

  return `${AUTOCOMPLETE_ENDPOINT}/v1/autocomplete?${searchParameters.toString()}`;
};

export const hereMapsApi = createApi({
  reducerPath: 'hereMapsApi',
  keepUnusedDataFor: 60 * 60 * 24 * 7,
  baseQuery: baseQueryRetry,
  endpoints: (build) => ({
    reverseGeocoding: build.query<ReverseGeocodingLocationItem[], LatitudeLongitude>({
      query: ({ latitude, longitude }) => ({
        url: `${REVERSE_GEOCODE_ENDPOINT}/v1/revgeocode?lang=${USER_LANGUAGE}&at=${latitude},${longitude},250&limit=1&apiKey=${HERE_MAPS_API_KEY}`,
      }),
      transformResponse: (response: ReverseGeocodingResponse) => response.items,
    }),
    multipleReverseGeocoding: build.mutation<MultipleReverseGeocodingResult[], MultipleReverseGeocodingArguments>({
      query: (items) => ({
        url: `${MULTIPLE_REVERSE_GEOCODE_ENDPOINT}/v1/multi-revgeocode?lang=${USER_LANGUAGE}&apiKey=${HERE_MAPS_API_KEY}`,
        method: 'POST',
        body: items.map(({ id, latitude, longitude }) => `id=${id}&at=${latitude},${longitude}`).join('\n'),
      }),
      transformResponse: (response: MultipleReverseGeocodingResponse) => response.results,
    }),
    getAddresses: build.query<GetAddressesItem[], GetAddressesArguments>({
      query: ({ searchTerm }: { searchTerm: string }) => ({
        url: getAutocompleteUrl({ searchTerm }),
      }),
      transformResponse: (response: GetAddressesResponse) => response.items,
    }),
    lookupAddress: build.query<LookupAddressResponse, LookupAddressArguments>({
      query: ({ id }) => ({
        url: `${LOOKUP_ENDPOINT}/v1/lookup?lang=${USER_LANGUAGE}&id=${id}&apiKey=${HERE_MAPS_API_KEY}`,
      }),
    }),
  }),
});

export const {
  useReverseGeocodingQuery,
  useMultipleReverseGeocodingMutation,
  useLazyReverseGeocodingQuery,
  useLazyGetAddressesQuery,
  useLookupAddressQuery,
} = hereMapsApi;
